import React from 'react';
import PropTypes from 'prop-types';
import { has, get } from 'lodash';
import numeral from 'numeral';
import { css } from '@bd/bdui-styles/emotion';
import { Image } from '@bd/bdcore-utilities';
import { Link } from '@bd/bdcore-named-routes';
import withData from '@bd/bdcore-data';
import { colors } from '../styles';

const styles = css`
  text-align: left;
  width: 100%;
  margin: 0 0 30px 0;
  .HomeCard_wrapper {
    display: flex;
  }
  .HomeCard_inner {
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: #FFFFFF;
  }
  .HomeCard_media {
    position: relative;
    img {
      width: 100%;
      height: auto;
    }
  }
  .HomeCard_soldBanner {
    position: relative;
    top: 0;
    right: 0;
    padding: 10px 15px;
    color: #FFFFFF;
    content: 'SOLD!';
    font-weight: 700;
    letter-spacing: 1px;
    background-color: ${colors.color1};
    z-index: 5;
  }
  .HomeCard_content {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    background-color: ${colors.color4};
    padding: 30px 20px 0 20px;
    h4 {
      margin: 0 0 5px 0;
      font-weight: 700;
      font-size: 24px;
      line-height: 24px;
      span {
        margin: 0 0 5px 0;
        font-size: 15px;
        font-weight: 300;
      }
      a {
        color: ${colors.color5};
        transition: all .3s ease;
        &:hover {
          color: ${colors.color2};
        }
      }
    }
  }
  .HomeCard_modelHours {
    font-size: 15px;
    font-weight: 300;
  }
  .HomeCard_modelHoursLabel {
    font-weight: 700;
  }
  .HomeCard_priceWrapper {
    border-top: 1px solid ${colors.color4Darkest};
    border-bottom: 1px solid ${colors.color4Darkest};
    display: flex;
    width: 45%;
    background-color: ${colors.color3Light};
    align-items: center;
    justify-content: center;
  }
  .HomeCard_priceValue {
    font-size: 19px;
    font-weight: 300;
    margin: 0;
  }
  .HomeCard_textList {
    list-style: none;
    padding: 0;
    margin: 0;
    border-bottom: 1px solid ${colors.color4Darkest};
    flex-grow: 1;
    li {
      font-size: 13px;
      font-weight: 300;
      line-height: 1;
      border-top: 1px solid ${colors.color4Darkest};
      padding: 10px;
      a {
        color: ${colors.color2};
      }
    }
  }
  .HomeCard_iconList {
    display: flex;
    list-style: none;
    margin: 0 0 15px 0;
    padding: 0;
    li {
      border-right: 1px solid ${colors.color4Darkest};
      width: 25%;
      text-align: center;
      padding: 12px 0;
      font-size: 14px;
      font-weight: 300;
      line-height: 18px;
      & > span {
        display: block;
      }
      &:last-child {
        border: 0;
      }
      .HomeCard_iconListIcon {
        margin: 0 0 4px 0;
      }
      .HomeCard_iconListValue {
        font-weight: 700;
      }
      .lighter {
        font-weight: 400;
      }
      a {
        transition: all .3s ease;
        color: ${colors.color5};
        font-weight: 700;
        &:hover {
          color: ${colors.color2};
        }
      }
      .HomeCard_linkIcon {
        width: 18px;
        height: 18px;
        margin: 5px 0 0 0;
      }
    }
  }
  .HomeCard_buttonWrapper {
    display: flex;
    justify-content: flex-end;
    margin: 0;
  }
  .HomeCard_mapButton {
    width: 40px;
    height: 40px;
    background-color: #6C6C6C;
    background-image: url('/images/icon-map-pin-white.svg');
    background-position: center center;
    background-repeat: no-repeat;
  }
  .HomeCard_detailButton {
    flex-grow: 1;
    height: 40px;
    line-height: 40px;
    text-align: center;
    background-color: ${colors.color2};
    color: #FFFFFF;
    font-weight: 700;
    font-size: 15px;
  }
`;

function HomeCard({ community, home, plan, widget }) {
  const background = {};
  if (has(home, 'photos[0].contentUrl')) {
    background.backgroundImage = get(home, 'photos[0].contentUrl');
  } else {
    background.backgroundImage = get(widget, 'default_image.contentUrl');
  }
  return (
    <div className={styles}>
      <div className="HomeCard_wrapper">
        <div className="HomeCard_inner">
          <div className="HomeCard_media">
            <Link name="home-detail" home={home}>
              <Image
                src={background.backgroundImage}
                alt={get(home, 'address.streetAddress')}
                width={475}
                height={250}
                exact
              />
            </Link>
          </div>
          <div className="HomeCard_content">
            <h4>
              <Link name="home-detail" home={home}>
                {get(home, 'address.streetAddress')}<br />
                <span>{get(home, 'address.addressLocality')}, {get(home, 'address.addressRegion')} {get(home, 'address.postalCode')}</span>
              </Link>
            </h4>
            {
              get(home, 'isModel') === true &&
              <h5 className="HomeCard_modelHours">
                <span className="HomeCard_modelHoursLabel">Model Home </span>
                {
                  get(home, 'isModelHours') &&
                  <span>
                    <span className="HomeCard_modelHoursLabel">Hours: </span>
                    {get(home, 'isModelHours')}
                  </span>
                }
              </h5>
            }
            <div className="d-flex mt-2">
              {
                get(home, 'price') > 10000 &&
                <div className="HomeCard_priceWrapper">
                  <span className="HomeCard_priceValue">{numeral(get(home, 'price')).format('$0,0')}</span>
                </div>
              }
              <ul className="HomeCard_textList">
                {
                  get(plan, 'name') &&
                  <li>Floor Plan: <Link name="plan-detail" plan={plan}>{get(plan, 'name')}</Link></li>
                }
                {
                  get(community, 'name') &&
                  <li>Community: <Link name="community-detail" community={community}>{get(community, 'name')}</Link></li>
                }
                {
                  get(home, 'status') &&
                  <li>Status: {get(home, 'status')}</li>
                }
              </ul>
            </div>
            <ul className="HomeCard_iconList">
              {
                get(home, 'beds') &&
                <li>
                  <span className="HomeCard_iconListIcon"><img src="/images/icon-beds.svg" alt="" /></span>
                  <span className="HomeCard_iconListValue">{get(home, 'beds')}</span>
                  <span className="HomeCard_iconListLabel">Beds</span>
                </li>
              }
              {
                get(home, 'bathsFull') &&
                <li>
                  <span className="HomeCard_iconListIcon"><img src="/images/icon-baths.svg" alt="" /></span>
                  <span className="HomeCard_iconListValue">
                    <span>{get(home, 'bathsFull') + (get(home, 'bathsHalf', 0) * 0.5)}</span>
                  </span>
                  <span className="HomeCard_iconListLabel">Baths</span>
                </li>
              }
              {
                get(home, 'sqft') &&
                <li>
                  <span className="HomeCard_iconListIcon"><img src="/images/icon-sqft.svg" alt="" /></span>
                  <span className="HomeCard_iconListValue">{numeral(get(home, 'sqft')).format('0,0')}</span>
                  <span className="HomeCard_iconListLabel">SQ FT</span>
                </li>
              }
              <li>
                <Link name="home-detail" home={home}>
                  View<br />Details<br />
                  <img src="/images/icon-arrow-gold.svg" className="HomeCard_linkIcon" alt="" />
                </Link>
              </li>
            </ul>
            {/* <div className="HomeCard_buttonWrapper">
              <Link className="HomeCard_mapButton" />
              <Link className="HomeCard_detailButton">Visit Community</Link>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}

HomeCard.propTypes = {
  home: PropTypes.object.isRequired,
  plan: PropTypes.object,
  community: PropTypes.object,
  widget: PropTypes.object.isRequired,
};

HomeCard.defaultProps = {
  plan: undefined,
  community: undefined,
};

export default withData(
  {
    home: true,
    plan: true,
    community: true,
    widget: 'default_image',
  },
  {
    required: {
      home: true,
    },
  },
)(HomeCard);



// WEBPACK FOOTER //
// ./src/components/HomeCard.js